/**
 * AboutVegr — pre-launch trust section. Three pillars on the left rail,
 * an animated Norway route map on the right that visualises the geospatial
 * matching the marketplace is built around.
 */

import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";

import { Container } from "@/components/ui/Container";

import { NorwayRouteMap } from "./NorwayRouteMap";

const aboutKeys = ["why", "who", "trust"] as const;

const fadeUp = {
  hidden: { opacity: 0, y: 24 },
  visible: { opacity: 1, y: 0, transition: { type: "spring" as const, stiffness: 100, damping: 20 } },
};

export function AboutVegr() {
  const { t } = useTranslation();

  return (
    <section
      className="relative isolate flex min-h-[100dvh] items-center overflow-hidden bg-slate-950 py-24 text-slate-50 lg:py-32"
      id="om-vegr"
    >
      <div className="pointer-events-none absolute inset-0 bg-[linear-gradient(to_bottom,#020617,#06101e_35%,#020617)]" />
      <div className="pointer-events-none absolute inset-0 bg-[linear-gradient(to_right,rgba(96,165,250,0.05)_1px,transparent_1px),linear-gradient(to_bottom,rgba(96,165,250,0.04)_1px,transparent_1px)] bg-[size:64px_64px] opacity-60" />

      <Container className="relative max-w-7xl">
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-100px" }}
          variants={fadeUp}
          className="grid grid-cols-1 gap-8 lg:grid-cols-[0.55fr_0.45fr] lg:items-end"
        >
          <div>
            <span className="font-mono text-[11px] uppercase tracking-[0.22em] text-brand-300">
              {t("about.label")}
            </span>
            <h2 className="mt-5 max-w-[16ch] text-4xl font-bold leading-[1.02] tracking-tight text-slate-50 md:text-6xl">
              {t("about.title")}
            </h2>
          </div>
          <p className="max-w-xl text-base leading-relaxed text-slate-300 md:text-lg lg:justify-self-end">
            {t("about.body")}
          </p>
        </motion.div>

        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-80px" }}
          variants={fadeUp}
          className="mt-14 grid grid-cols-1 gap-12 lg:mt-20 lg:grid-cols-[0.42fr_0.58fr] lg:items-center lg:gap-16"
        >
          <ul className="divide-y divide-slate-800/70">
            {aboutKeys.map((key, idx) => (
              <li
                key={key}
                className="grid grid-cols-[auto_1fr] gap-x-6 py-7 first:pt-0 last:pb-0 sm:gap-x-8 lg:py-8"
              >
                <span className="font-mono text-[10px] uppercase tracking-[0.22em] text-brand-300/80">
                  {String(idx + 1).padStart(2, "0")}
                </span>
                <div>
                  <h3 className="text-xl font-semibold tracking-tight text-slate-50 md:text-2xl">
                    {t(`about.items.${key}.title`)}
                  </h3>
                  <p className="mt-3 max-w-[42ch] text-sm leading-relaxed text-slate-400 md:text-base">
                    {t(`about.items.${key}.body`)}
                  </p>
                </div>
              </li>
            ))}
          </ul>

          <div className="mx-auto w-full max-w-[460px] lg:mx-0 lg:max-w-[520px] lg:justify-self-end">
            <NorwayRouteMap />
          </div>
        </motion.div>
      </Container>
    </section>
  );
}
